import React, { useState, useEffect } from 'react';
import { 
  Brain, TrendingUp, AlertTriangle, Shield, BarChart3, 
  Users, Car, Calendar, DollarSign, Camera, Upload,
  X, ChevronRight, Activity, Target, Zap
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Modal from '../components/Common/Modal';

const AIDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('risk-scores');
    const [riskScores, setRiskScores] = useState([]);
    const [latePredictions, setLatePredictions] = useState([]);
    const [churnForecasts, setChurnForecasts] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [selectedLease, setSelectedLease] = useState(null);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [damageModal, setDamageModal] = useState(false);
    const [damageData, setDamageData] = useState({
        before_image: null,
        after_image: null,
        asset_unit_id: ''
    });
    const [assetUnits, setAssetUnits] = useState([]);
    const [leases, setLeases] = useState([]);
    const [assets, setAssets] = useState([]);
    const [damageResult, setDamageResult] = useState(null);

    useEffect(() => {
        fetchRiskScores();
        fetchAssetUnits();
        fetchLeases();
        fetchAssets();
    }, []);

    const fetchRiskScores = async () => {
        setLoading(true);
        try {
            const response = await api.get('/ai/risk-scores');
            setRiskScores(response.data.data || []);
        } catch (error) {
            console.error('Error fetching risk scores:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetUnits = async () => {
        try {
            const response = await api.get('/assets');
            const assetsData = response.data.data?.data || response.data.data || [];
            const units = [];
            for (const asset of assetsData) {
                try {
                    const unitsResponse = await api.get(`/assets/${asset.id}/units`);
                    units.push(...unitsResponse.data.data.map(u => ({ 
                        ...u, 
                        asset_name: asset.name 
                    })));
                } catch {
                    // skip assets that can't fetch units
                }
            }
            setAssetUnits(units);
        } catch (error) {
            console.error('Error fetching asset units:', error);
        }
    };

    const fetchLeases = async () => {
        try {
            const response = await api.get('/leases?status=active');
            // leases endpoint returns { success, data: { data: [...], pagination: {} } }
            const leasesData = response.data.data?.data || response.data.data || [];
            setLeases(Array.isArray(leasesData) ? leasesData : []);
        } catch (error) {
            console.error('Error fetching leases:', error);
        }
    };

    const fetchAssets = async () => {
        try {
            const response = await api.get('/assets');
            const assetsData = response.data.data?.data || response.data.data || [];
            setAssets(assetsData);
        } catch (error) {
            console.error('Error fetching assets:', error);
        }
    };

    const checkTenantRisk = async (userId) => {
        setLoading(true);
        try {
            const response = await api.get(`/ai/risk-score/${userId}`);
            alert(`Risk Score: ${response.data.data.risk_score}\nRisk Level: ${response.data.data.risk_level}\nRecommendations: ${response.data.data.recommendations.join(', ')}`);
        } catch (error) {
            console.error('Error checking risk:', error);
            alert('Failed to get risk score');
        } finally {
            setLoading(false);
        }
    };

    const checkLatePayment = async (leaseId) => {
        setLoading(true);
        try {
            const response = await api.get(`/ai/late-payment/${leaseId}`);
            const data = response.data.data;
            alert(`Late Payment Probability: ${(data.probability * 100).toFixed(1)}%\nRisk Level: ${data.risk_level}\nSuggested Action: ${data.suggested_action}`);
        } catch (error) {
            console.error('Error checking late payment:', error);
            alert('Failed to get late payment prediction');
        } finally {
            setLoading(false);
        }
    };

    const checkChurn = async (assetId) => {
        setLoading(true);
        try {
            const response = await api.get(`/ai/churn/${assetId}`);
            const data = response.data.data;
            alert(`Churn Probability: ${(data.churn_probability * 100).toFixed(1)}%\nExpected Remaining Months: ${data.expected_remaining_months}\nSuggested Action: ${data.suggested_action}`);
        } catch (error) {
            console.error('Error checking churn:', error);
            alert('Failed to get churn forecast');
        } finally {
            setLoading(false);
        }
    };

    const handleDamageDetection = async (e) => {
        e.preventDefault();
        setLoading(true);
        setDamageResult(null);
        
        try {
            const formData = new FormData();
            formData.append('before_image', damageData.before_image);
            formData.append('after_image', damageData.after_image);
            formData.append('asset_unit_id', damageData.asset_unit_id);
            
            const response = await api.post('/ai/detect-damage', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setDamageResult(response.data.data);
        } catch (error) {
            console.error('Error detecting damage:', error);
            alert('Failed to detect damage');
        } finally {
            setLoading(false);
        }
    };

    const getRiskColor = (score) => {
        if (score < 30) return 'text-green-600 bg-green-50';
        if (score < 60) return 'text-yellow-600 bg-yellow-50';
        return 'text-red-600 bg-red-50';
    };

    const getRiskLevel = (score) => {
        if (score < 30) return 'Low Risk';
        if (score < 60) return 'Medium Risk';
        return 'High Risk';
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Brain className="h-8 w-8 text-indigo-600 mr-3" />
                    AI Insights & Analytics
                </h1>
                <p className="text-gray-600 mt-1">Machine learning powered predictions and risk assessment</p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                    {[
                        { id: 'risk-scores', label: 'Risk Scores', icon: Shield },
                        { id: 'late-payments', label: 'Late Payments', icon: AlertTriangle },
                        { id: 'churn', label: 'Churn Forecast', icon: TrendingUp },
                        { id: 'damage', label: 'Damage Detection', icon: Camera }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <tab.icon className="h-5 w-5 mr-2" />
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Risk Scores Tab */}
            {activeTab === 'risk-scores' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm opacity-90">High Risk Tenants</p>
                                    <p className="text-3xl font-bold">{riskScores.filter(r => r.risk_level === 'High').length}</p>
                                </div>
                                <AlertTriangle className="h-10 w-10 opacity-80" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-yellow-500 to-orange-400 rounded-lg p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm opacity-90">Medium Risk Tenants</p>
                                    <p className="text-3xl font-bold">{riskScores.filter(r => r.risk_level === 'Medium').length}</p>
                                </div>
                                <Activity className="h-10 w-10 opacity-80" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-lg p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm opacity-90">Low Risk Tenants</p>
                                    <p className="text-3xl font-bold">{riskScores.filter(r => r.risk_level === 'Low').length}</p>
                                </div>
                                <Shield className="h-10 w-10 opacity-80" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold">Tenant Risk Assessment</h3>
                            <p className="text-sm text-gray-500">AI-powered risk scores based on payment history and behavior</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Score</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {riskScores.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                                                No risk scores available yet. Scores are calculated as data accumulates.
                                            </td>
                                        </tr>
                                    ) : (riskScores.map((score, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-medium text-gray-900">Tenant #{score.entity_id}</p>
                                                    <p className="text-sm text-gray-500">ID: {score.entity_id}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex-1 h-2 bg-gray-200 rounded-full w-24">
                                                        <div 
                                                            className={`h-2 rounded-full ${
                                                                score.prediction_value.risk_score < 30 ? 'bg-green-500' :
                                                                score.prediction_value.risk_score < 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                            style={{ width: `${score.prediction_value.risk_score}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm font-medium">{score.prediction_value.risk_score}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(score.prediction_value.risk_score)}`}>
                                                    {getRiskLevel(score.prediction_value.risk_score)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => checkTenantRisk(score.entity_id)}
                                                    className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center"
                                                >
                                                    View Details <ChevronRight className="h-4 w-4 ml-1" />
                                                </button>
                                            </td>
                                        </tr>
                                    )))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Late Payments Tab */}
            {activeTab === 'late-payments' && (
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-center py-12">
                        <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Late Payment Predictions</h3>
                        <p className="text-gray-500 mb-6">AI analyzes payment patterns to predict potential late payments</p>
                        <div className="max-w-md mx-auto">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Lease</label>
                            <select
                                value={selectedLease || ''}
                                onChange={(e) => setSelectedLease(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 mb-4"
                            >
                                <option value="">Select a lease...</option>
                                {leases.length === 0 && (
                                    <option value="" disabled>No leases available</option>
                                )}
                                {leases.map(lease => (
                                    <option key={lease.id} value={lease.id}>
                                        {lease.asset_name || 'Unknown Asset'} 
                                        {lease.unit_number ? ` - ${lease.unit_number}` : ''}
                                        {lease.first_name ? ` (${lease.first_name} ${lease.last_name || ''})` : ''}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => selectedLease && checkLatePayment(selectedLease)}
                                disabled={!selectedLease || loading}
                                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? 'Analyzing...' : 'Predict Late Payment Risk'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Churn Forecast Tab */}
            {activeTab === 'churn' && (
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-center py-12">
                        <TrendingUp className="h-16 w-16 text-purple-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Customer Churn Forecast</h3>
                        <p className="text-gray-500 mb-6">Predict which tenants are likely to leave and take proactive action</p>
                        <div className="max-w-md mx-auto">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Asset</label>
                            <select
                                value={selectedAsset || ''}
                                onChange={(e) => setSelectedAsset(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 mb-4"
                            >
                                <option value="">Select an asset...</option>
                                {assets.length === 0 && (
                                    <option value="" disabled>No assets available</option>
                                )}
                                {assets.map(asset => (
                                    <option key={asset.id} value={asset.id}>
                                        {asset.name} {asset.type ? `(${asset.type})` : ''}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => selectedAsset && checkChurn(selectedAsset)}
                                disabled={!selectedAsset || loading}
                                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? 'Forecasting...' : 'Predict Churn Risk'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Damage Detection Tab */}
            {activeTab === 'damage' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center">
                            <Camera className="h-5 w-5 mr-2 text-indigo-600" />
                            AI Damage Detection
                        </h3>
                        <p className="text-sm text-gray-600 mb-6">
                            Upload before and after photos to automatically detect damages, scratches, dents, and missing items
                        </p>

                        <form onSubmit={handleDamageDetection} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Asset Unit</label>
                                <select
                                    value={damageData.asset_unit_id}
                                    onChange={(e) => setDamageData({ ...damageData, asset_unit_id: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 p-2"
                                    required
                                >
                                    <option value="">Select asset unit...</option>
                                    {assetUnits.length === 0 && (
                                        <option value="" disabled>No asset units available</option>
                                    )}
                                    {assetUnits.map(unit => (
                                        <option key={unit.id} value={unit.id}>
                                            {unit.asset_name} - {unit.unit_number || 'Main Unit'}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Before Photo</label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        {damageData.before_image ? (
                                            <div className="relative">
                                                <img 
                                                    src={URL.createObjectURL(damageData.before_image)} 
                                                    alt="Before" 
                                                    className="max-h-48 mx-auto rounded"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setDamageData({ ...damageData, before_image: null })}
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer block">
                                                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                                                <span className="text-sm text-gray-500">Click to upload before photo</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => setDamageData({ ...damageData, before_image: e.target.files[0] })}
                                                    className="hidden"
                                                    required
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">After Photo</label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        {damageData.after_image ? (
                                            <div className="relative">
                                                <img 
                                                    src={URL.createObjectURL(damageData.after_image)} 
                                                    alt="After" 
                                                    className="max-h-48 mx-auto rounded"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setDamageData({ ...damageData, after_image: null })}
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer block">
                                                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                                                <span className="text-sm text-gray-500">Click to upload after photo</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => setDamageData({ ...damageData, after_image: e.target.files[0] })}
                                                    className="hidden"
                                                    required
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !damageData.before_image || !damageData.after_image}
                                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? 'Analyzing Images...' : 'Detect Damages'}
                            </button>
                        </form>
                    </div>

                    {/* Damage Detection Results */}
                    {damageResult && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">Damage Detection Results</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div className={`p-4 rounded-lg ${damageResult.damages_found ? 'bg-red-50' : 'bg-green-50'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">Status</p>
                                            <p className={`text-xl font-bold ${damageResult.damages_found ? 'text-red-600' : 'text-green-600'}`}>
                                                {damageResult.damages_found ? 'Damages Detected' : 'No Damages Found'}
                                            </p>
                                        </div>
                                        {damageResult.damages_found ? <AlertTriangle className="h-8 w-8 text-red-500" /> : <Shield className="h-8 w-8 text-green-500" />}
                                    </div>
                                </div>
                                <div className="p-4 bg-purple-50 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">Severity</p>
                                            <p className="text-xl font-bold text-purple-600 capitalize">{damageResult.severity}</p>
                                        </div>
                                        <Activity className="h-8 w-8 text-purple-500" />
                                    </div>
                                </div>
                            </div>

                            {damageResult.damages && damageResult.damages.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3">Detected Damages</h4>
                                    <div className="space-y-2">
                                        {damageResult.damages.map((damage, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div>
                                                    <p className="font-medium capitalize">{damage.type}</p>
                                                    <p className="text-sm text-gray-500 capitalize">Severity: {damage.severity}</p>
                                                </div>
                                                <span className="text-sm text-gray-600">Confidence: {(damage.confidence * 100).toFixed(0)}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {damageResult.face_match !== undefined && (
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-blue-800">
                                        Face Match: {damageResult.face_match ? '✓ Same person in both photos' : '⚠ Different people detected'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {loading && <LoadingSpinner size="lg" />}
        </div>
    );
};

export default AIDashboard;
